import React from 'react'
import { FastField, useFormikContext } from 'formik'
import { Box, FormHelperText, Grid } from '@mui/material'
import { CustomTextField, CustomCheckboxWithoutInitialValue } from 'src/form'

import { Section, SubSection } from '../common/Section'
import { IntakeNoteComprehensiveFormType } from './IntakeNoteComprehensiveFormSchema'

export function RiskAssessmentForm() {
  const { values, errors } = useFormikContext<IntakeNoteComprehensiveFormType>()

  return (
    <Section title="Risk Assessment">
      <SubSection>
        <FastField
          fullWidth
          component={CustomCheckboxWithoutInitialValue}
          name="RISK_ASSESSMENT.BASE.DENY_SI_SIB_SA_HI"
          label="Participant denies current or past SI, SIB, SA, HI"
        />
        <FastField
          fullWidth
          component={CustomCheckboxWithoutInitialValue}
          name="RISK_ASSESSMENT.BASE.SI_WITH_INTENT_PLAN_CRISIS_INITIATED"
          label="Participant endorses current SI with intent and plan. Crisis intervention initiated."
        />
        <FastField
          fullWidth
          component={CustomCheckboxWithoutInitialValue}
          name="RISK_ASSESSMENT.BASE.OTHER"
          label="Other"
          helperText="E.g., history of SI/SA/HI; current SI without plan/intent - details documented below."
        />
        {values.RISK_ASSESSMENT.BASE.OTHER && (
          <FastField
            fullWidth
            component={CustomTextField}
            name="RISK_ASSESSMENT.BASE.OTHER_DETAILS"
            label="Other details"
            multiline
            minRows={4}
          />
        )}
        {errors.RISK_ASSESSMENT?.BASE && !errors.RISK_ASSESSMENT.BASE.OTHER_DETAILS && (
          <FormHelperText error>Please select at least one of the options above.</FormHelperText>
        )}
      </SubSection>

      <SubSection title="Protective Factors Identified">
        <Box mt={2}>
          <Grid container columnSpacing={2.5} rowSpacing={0}>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.STRONG_SOCIAL_SUPPORT"
                label="Strong social support"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.ENGAGEMENT_IN_THERAPY"
                label="Engagement in therapy"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.COPING_SKILLS"
                label="Coping skills"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.FUTURE_ORIENTED_THINKING"
                label="Future-oriented thinking"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.MOTIVITED_FOR_TREATMENT"
                label="Motivated for treatment"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.RELIGIOUS_BELIEFS"
                label="Religious/spiritual beliefs"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.OTHER"
                label="Other protective factors"
              />
            </Grid>
            {values.RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.OTHER && (
              <Grid item xs={12} md={12}>
                <FastField
                  fullWidth
                  component={CustomTextField}
                  name="RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.OTHER_FACTOR"
                  label="Other protective factors"
                  multiline
                  minRows={4}
                />
              </Grid>
            )}
          </Grid>
        </Box>
        {errors.RISK_ASSESSMENT?.PROTECTIVE_FACTORS_IDENTIFIED &&
          !errors.RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.OTHER_FACTOR && (
            <FormHelperText error>Please select at least one of the options above.</FormHelperText>
          )}
      </SubSection>

      <SubSection title="Next Steps">
        <Box mt={2}>
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="RISK_ASSESSMENT.NEXT_STEPS.SAFETY_PLAN_REVIEWED_EMAILLED"
            label="Safety plan developed, reviewed and emailed to client."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="RISK_ASSESSMENT.NEXT_STEPS.SAFETY_PLAN_DOCUMENTED_IN_RECORD"
            label="Safety plan documented in medical record."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="RISK_ASSESSMENT.NEXT_STEPS.LAW_ENFORCEMENT_OR_EMERGENCY_CONTACT_NOTIFIED"
            label="Law enforcement/emergency contact notified due to imminent risk."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="RISK_ASSESSMENT.NEXT_STEPS.NO_FURTHER_ASSISTANCE_NEEDED"
            label="Patient presents as low risk, no safety plan needed at this time."
          />
          <FastField
            fullWidth
            component={CustomTextField}
            name="RISK_ASSESSMENT.NEXT_STEPS.NEXT_STEPS_DETAILS"
            label="Next steps details"
            helperText="Include the safety plan (if applicable) or other next steps."
            multiline
            minRows={4}
          />
        </Box>
        {errors.RISK_ASSESSMENT?.NEXT_STEPS && (
          <FormHelperText error>Please select at least one of the options above.</FormHelperText>
        )}
      </SubSection>
    </Section>
  )
}
